/** Stroke style for crochet stitch symbols: a width multiplier on the
 *  renderer's base stroke (STITCH_STROKE_SCREEN_PX, zoom-independent) and a
 *  line-end style. Sparse — only deviations are stored — and resolved per key
 *  through three tiers: stitch override → diagram global → factory.
 *  The factory value is pixel-identical to the pre-feature renderer. */

export type StrokeCap = 'round' | 'square' | 'butt';
export const STROKE_CAPS: readonly StrokeCap[] = ['round', 'square', 'butt'];

export type StrokeStyle = { widthScale?: number; cap?: StrokeCap };
export type ResolvedStrokeStyle = Required<StrokeStyle>;

export const FACTORY_STROKE_STYLE: ResolvedStrokeStyle = { widthScale: 1, cap: 'round' };
export const STROKE_WIDTH_SCALE_MIN = 0.5;
export const STROKE_WIDTH_SCALE_MAX = 3;
export const STROKE_WIDTH_SCALE_STEP = 0.25;

const CAP_SET: ReadonlySet<string> = new Set(STROKE_CAPS);

export function isStrokeCap(v: unknown): v is StrokeCap {
    return typeof v === 'string' && CAP_SET.has(v);
}

export function clampStrokeWidthScale(v: number): number {
    return Math.min(STROKE_WIDTH_SCALE_MAX, Math.max(STROKE_WIDTH_SCALE_MIN, v));
}

function isValidWidthScale(v: unknown): v is number {
    return typeof v === 'number' && Number.isFinite(v)
        && v >= STROKE_WIDTH_SCALE_MIN && v <= STROKE_WIDTH_SCALE_MAX;
}

/** Shape-validate an untrusted value (server, localStorage, stored doc).
 *  Out-of-range widths and unknown caps are dropped, never clamped — a bad
 *  persisted value falls back to the next tier rather than to an edge. */
export function sanitizeStrokeStyle(raw: unknown): StrokeStyle {
    if (typeof raw !== 'object' || raw == null) return {};
    const r = raw as Record<string, unknown>;
    const out: StrokeStyle = {};
    if (isValidWidthScale(r.widthScale)) out.widthScale = r.widthScale;
    if (isStrokeCap(r.cap)) out.cap = r.cap;
    return out;
}

export function isEmptyStrokeStyle(s: StrokeStyle | undefined): boolean {
    return s == null || (s.widthScale == null && s.cap == null);
}

export function strokeStyleEqual(a: StrokeStyle | undefined, b: StrokeStyle | undefined): boolean {
    return (a?.widthScale ?? null) === (b?.widthScale ?? null) && (a?.cap ?? null) === (b?.cap ?? null);
}

export function resolveStrokeStyle(
    override: StrokeStyle | undefined, diagram: StrokeStyle | undefined,
): ResolvedStrokeStyle {
    return {
        widthScale: override?.widthScale ?? diagram?.widthScale ?? FACTORY_STROKE_STYLE.widthScale,
        cap: override?.cap ?? diagram?.cap ?? FACTORY_STROKE_STYLE.cap,
    };
}

export function strokeJoinFor(cap: StrokeCap): 'round' | 'miter' {
    return cap === 'round' ? 'round' : 'miter';
}

/** The per-stitch override carried on a document node, as a StrokeStyle;
 *  undefined when the node carries neither field. Structural so it accepts a
 *  Tbc and a synthetic preview Tbc alike. */
export function strokeOverrideOf(
    t: { strokeWidthScale?: number; strokeCap?: StrokeCap },
): StrokeStyle | undefined {
    if (t.strokeWidthScale == null && t.strokeCap == null) return undefined;
    return {
        ...(t.strokeWidthScale != null ? { widthScale: t.strokeWidthScale } : {}),
        ...(t.strokeCap != null ? { cap: t.strokeCap } : {}),
    };
}
